import { useState, useEffect, useCallback } from 'react';
import pipelineService from '../services/pipelineService';

/**
 * usePipeline Hook
 * 
 * What this does:
 * - Manages pipeline state in React components
 * - Provides functions for pipeline operations
 * - Handles loading and error states
 * 
 * Usage:
 * const { pipeline, loading, error, reload, moveCard } = usePipeline(pipelineId);
 */

const usePipeline = (pipelineId) => {
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load pipeline data
  const loadPipeline = useCallback(async () => {
    if (!pipelineId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await pipelineService.getDetailed(pipelineId);
      setPipeline(data);
    } catch (err) {
      console.error('Error loading pipeline:', err);
      setError(err.message || 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  // Load on mount and when pipelineId changes
  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  // Move card to different stage
  const moveCard = useCallback(async (cardId, newStageId) => {
    try {
      await pipelineService.moveCard(cardId, newStageId);
      await loadPipeline(); // Reload to show updated board
    } catch (err) {
      console.error('Error moving card:', err);
      throw err;
    }
  }, [loadPipeline]);

  // Update card priority
  const updatePriority = useCallback(async (cardId, priority) => {
    try {
      await pipelineService.updateCardPriority(cardId, priority);
      await loadPipeline();
    } catch (err) {
      console.error('Error updating priority:', err);
      throw err;
    }
  }, [loadPipeline]);

  // Delete card
  const deleteCard = useCallback(async (cardId) => {
    try {
      await pipelineService.deleteCard(cardId);
      await loadPipeline();
    } catch (err) {
      console.error('Error deleting card:', err);
      throw err;
    }
  }, [loadPipeline]);

  // Assign user to card
  const assignUser = useCallback(async (cardId, userId) => {
    try {
      await pipelineService.assignUser(cardId, userId);
      await loadPipeline();
    } catch (err) {
      console.error('Error assigning user:', err);
      throw err;
    }
  }, [loadPipeline]);

  // Unassign user from card
  const unassignUser = useCallback(async (cardId, userId) => {
    try {
      await pipelineService.unassignUser(cardId, userId);
      await loadPipeline();
    } catch (err) {
      console.error('Error unassigning user:', err);
      throw err;
    }
  }, [loadPipeline]);

  return {
    pipeline,
    loading,
    error,
    reload: loadPipeline,
    moveCard,
    updatePriority,
    deleteCard,
    assignUser,
    unassignUser
  };
};

export default usePipeline;
